<template>
  <div
    class="plan-container-wrapper"
    :class="{ 'recommended-plan': recommendedPlan }"
    :style="recommendedPlan ? `border: 2px solid ${prismicProps.primaryColor || '#3cd699'}` : ''"
  >
    <div v-show="lifetime && selectedPlan.discount" class="discount-badge">
      -{{ selectedPlan.discount }}%
    </div>
    <div v-if="recommendedPlan" class="mt-4">
      <p class="recommended-plan-header m-0" :style="`color: ${prismicProps.secondaryColor}`">
        {{ $t('plans.common.cards.recommended') }}
      </p>
    </div>
    <div class="plan-container mt-2 flex-column" :class="{ highlighted }">
      <!-- eslint-enable max-len -->
      <b-row>
        <b-col class="title" :style="`color: ${prismicProps.primaryColor}`">
          <p>{{ selectedPlan.name }}</p>
          <p
            v-if="selectedPlan.experienceType !== getDesign && selectedPlan.subtitle"
            class="plan-subtitle"
          >
            {{ selectedPlan.subtitle }}
          </p>
        </b-col>
      </b-row>
      <b-row v-show="showSwitch" class="mt-4">
        <b-col class="d-flex align-items-center justify-content-center">
          <div class="position-relative d-flex align-items-center justify-content-center">
            <span class="period-text monthly mr-1" :class="{ active: !value }">
              {{ $t('plans.common.cards.monthly') }}
            </span>
          </div>
          <VxSwitch
            :color="prismicProps.primaryColor"
            :value="value"
            @change="$emit('input', $event.target.checked)"
          />
          <div class="position-relative d-flex align-items-center justify-content-center">
            <span class="period-text annual ml-1" :class="{ active: value }">
              <span>{{ $t('plans.common.cards.annual') }}</span>
              <span class="discount">
                <span v-show="value && selectedPlan.discount">-{{ selectedPlan.discount }}%</span>
              </span>
            </span>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          class="d-flex align-items-center justify-content-center flex-column"
          :class="{ 'no-switch': !showSwitch }"
        >
          <b-row class="align-items-baseline">
            <div class="currency align-self-center mr-1">{{ selectedPlan.currencySymbol }}</div>
            <div class="price">{{ priceBigNumber }}</div>
            <div class="price small">{{ priceSmallNumber }}</div>
            <div v-show="!value && showSwitch" class="per-period">
              {{ $t('plans.common.cards.perMonth') }}
            </div>
          </b-row>
          <b-row
            v-if="prismicProps.showPreviousPrice"
            class="previous-cost"
            :style="{ '--borderColor': prismicProps.primaryColor }"
          >
            <span>$</span><span>{{ previousPrice }}</span>
          </b-row>
          <b-row v-if="value && showSwitch" class="billed">
            <span> {{ selectedPlan.cost }} {{ $t('plans.common.cards.billedAnnualy') }} </span>
          </b-row>
          <b-row v-if="lifetime" class="billed">
            <span>
              {{ $t('plans.common.cards.billedLifetime') }}
            </span>
          </b-row>
        </b-col>
      </b-row>

      <b-row v-if="isSingleLicense && isDesignPlan" class="justify-content-center">
        <Checkbox @input="extended = !extended">
          {{ $t('plans.common.cards.forMerchandiseUse') }}
        </Checkbox>
      </b-row>

      <b-row v-if="hasSpecs" class="mt-4 justify-content-center">
        <b-col cols="11" class="specs d-flex flex-column align-items-start">
          <b-collapse
            v-for="(s, i) in selectedPlan.specs || []"
            :id="i < shownSpecsAmount ? '' : 'collapse-hidden'"
            :key="i"
            class="row mt-2"
            :visible="i < shownSpecsAmount"
          >
            <b-col cols="1">
              <Icon icon="tick" color="#a2a2a2" :width="10" :height="10" :exp-active="true" />
            </b-col>
            <b-col class="pl-0 text-left">
              {{ s.spec }}
              <b-button
                v-if="s.tooltip"
                :id="`btn-tooltip-${selectedPlan.id}-${i}`"
                class="btn-tooltip ml-1"
              >
                i
              </b-button>
            </b-col>
            <b-tooltip
              v-if="s.tooltip"
              custom-class="custom-tooltip"
              :target="`btn-tooltip-${selectedPlan.id}-${i}`"
              placement="top"
            >
              {{ s.tooltip }}
            </b-tooltip>
          </b-collapse>
        </b-col>
      </b-row>
      <b-row class="mt-4 justify-content-center">
        <b-col cols="11" class="specs d-flex flex-column align-items-start">
          <b-row>
            <b-col>
              <span
                v-b-toggle.collapse-hidden
                class="details cursor-pointer"
                :style="`color: ${prismicProps.tertiaryColor}`"
                @click="$ga.event('All Plans', 'Click See More Details', $route.fullPath)"
              >
                {{ $t('plans.common.cards.seeMoreDetails') }}
              </span>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row v-if="prismicProps.showBenefitsText" class="my-2 justify-content-center">
        <b-col
          cols="11"
          class="specs cursor-pointer"
          :style="`color: ${prismicProps.primaryColor}`"
          @click="goToTable"
        >
          {{ $t('plans.common.cards.blackFridayBenefits') }}
        </b-col>
      </b-row>
      <b-row v-if="selectedPlan.image" class="mt-4 justify-content-center">
        <b-col cols="11">
          <img
            :src="selectedPlan.image"
            :alt="$t('plans.common.cards.PODlogosAlt')"
            class="mt-2 w-100"
            loading="lazy"
          />
        </b-col>
      </b-row>
      <b-row class="mb-4 justify-content-center subscribe-container">
        <b-col class="button-container" cols="11">
          <Button v-bind="buttonProps" @click="subscribeClick">
            {{ buttonText }}
          </Button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from 'UI/Button';
import Checkbox from 'UI/Checkbox';
import Icon from 'UI/Icon';
import VxSwitch from 'UI/Switch';
import { getWindowScrollTop } from '~/utils/common.js';

export default {
  components: {
    Button,
    Checkbox,
    VxSwitch,
    Icon
  },
  props: {
    value: {
      type: Boolean,
      required: false,
      default: true
    },
    planIndex: {
      type: Number,
      required: false,
      default: 0
    },
    plan: {
      type: Object,
      required: true
    },
    isFreeUser: {
      type: Boolean,
      required: true
    },
    seeMoreLink: {
      type: String,
      required: false,
      default: ''
    },
    lifetime: {
      type: Boolean,
      default: false,
      required: false
    },
    isDesignPlan: {
      type: Boolean,
      default: false
    },
    prismicProps: {
      type: Object,
      default: () => {}
    },
    customPlan: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      shownSpecsAmount: 3,
      extended: false
    };
  },
  computed: {
    buttonProps() {
      let props = {};
      if (this.recommendedPlan || this.lifetime) {
        props = {
          enabled: this.buttonEnabled,
          width: 'full',
          class: 'w-100',
          gradient:
            'transparent linear-gradient(85deg, ' +
            this.prismicProps.buttonGradientColor1 +
            ' 0%, ' +
            this.prismicProps.buttonGradientColor2 +
            ' 100%) 0% 0% no-repeat padding-box;'
        };
        if (this.prismicProps.buttonGradientColor1 && this.prismicProps.buttonGradientColor2) {
          props.withGradient = true;
        }
      } else {
        props = {
          enabled: this.buttonEnabled,
          width: 'full',
          class: 'w-100',
          shape: 'outline',
          color: 'transparent',
          text: this.buttonTextColor,
          fontColor: this.prismicProps.tertiaryColor || '#3cd699'
        };
      }
      return props;
    },
    previousPrice() {
      return this.prismicProps?.previousPrice[this.planIndex];
    },
    recommendedPlan() {
      return this.planIndex == 1 && !this.lifetime;
    },
    ...mapGetters('experience', ['getExperience', 'getDesign']),
    highlighted() {
      return this.selectedPlan ? this.selectedPlan.highlighted : false;
    },
    selectedPlan() {
      if (this.plan) {
        if (this.value && this.plan.annual) {
          return this.plan.annual;
        } else if (!this.value && this.plan.monthly) {
          return this.plan.monthly;
        } else if (this.extended && this.plan.extended) {
          return this.plan.extended;
        } else if (!this.extended && this.plan.simple) {
          return this.plan.simple;
        } else if (this.lifetime || this.customPlan) {
          return this.plan;
        } else {
          return {};
        }
      } else {
        return {};
      }
    },
    showSwitch() {
      if (this.isSingleLicense || this.lifetime) {
        return false;
      } else {
        if (this.plan) {
          return this.plan.monthly && this.plan.annual;
        } else {
          return false;
        }
      }
    },
    hasSpecs() {
      return this.selectedPlan.specs && this.selectedPlan.specs.length > 0;
    },
    buttonEnabled() {
      return this.selectedPlan.upgrade || this.isSingleLicense;
    },
    buttonTextColor() {
      return this.buttonEnabled ? this.prismicProps.tertiaryColor || 'green' : 'white';
    },
    recommendedButtonTextColor() {
      return this.prismicProps.buttonGradientColor1 ? this.prismicProps.buttonGradientColor1 : '';
    },
    buttonText() {
      if (this.isSingleLicense) {
        return this.$t('plans.common.cards.buy');
      } else {
        if (this.$auth.loggedIn && !this.isFreeUser && this.selectedPlan.upgrade) {
          return this.$t('plans.common.cards.upgrade');
        } else {
          return this.$t('plans.common.cards.suscribe');
        }
      }
    },
    isSingleLicense() {
      return this.plan && (this.plan.simple || this.plan.extended) ? true : false;
    },
    isPlansPage() {
      const isDesignPlans = this.$urlNameChecker('design-plans');
      const isMerchPlans = this.$urlNameChecker('merch-plans');
      return isDesignPlans || isMerchPlans;
    },
    shownPrice() {
      if (this.lifetime) return [this.selectedPlan.cost, ''];
      var price = this.selectedPlan.cost;
      if (this.value && !this.isSingleLicense) {
        price = this.selectedPlan.cost / 12;
      }
      const noDecimals = Math.floor(price);
      const decimals = price - noDecimals;
      if (decimals > 0.1 && decimals <= 0.5) {
        return [noDecimals, '.50'];
      } else if (decimals > 0.5) {
        return [noDecimals, '.99'];
      } else {
        return [noDecimals];
      }
    },
    priceBigNumber() {
      return this.shownPrice[0];
    },
    priceSmallNumber() {
      return this.shownPrice.length == 2 ? this.shownPrice[1] : '';
    }
  },
  methods: {
    subscribeClick() {
      this.$emit('subscribe-click', this.selectedPlan);
      this.$ga.event('All Plans', 'Click Button Subscribe Top Plans', this.$route.fullPath);
    },
    goToTable() {
      const element = document.getElementById('compare-plans');
      if (element) {
        const headerOffset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + getWindowScrollTop() - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-tooltip {
  margin: 10px;
  .tooltip-inner {
    padding: 10px;
  }
}
.plan-container-wrapper {
  box-shadow: 0px 0px 40px -10px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  background-color: white;
  display: flex;
  min-height: 80%;
  margin: 3.5em 0px 3em 0px;
  margin-top: calc(21px + 1.5em);
  flex-direction: column;
  align-items: center;
  width: 31%;
  @include lg {
    width: 45%;
  }
  @include md {
    width: 100%;
  }
  &.recommended-plan {
    margin: 0;
    padding-bottom: 3rem;
  }
  .recommended-plan-header {
    color: #3cd699;
    letter-spacing: 4px;
    font-size: 14px;
  }
  .discount-badge {
    background-color: var(--exp-color);
    color: white;
    padding: 3px 8px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    z-index: 1;
  }
}
.plan-container {
  position: relative;
  display: flex;
  min-height: 100%;
  height: max-content;
  padding: 1.5rem 3rem 3rem;
  border-radius: 5px;
  text-align: center;
  color: rgba(0, 0, 0, 0.1);
  width: 100%;
  &.highlighted {
    color: rgba(32, 185, 181, 0.4);
  }
  .title {
    letter-spacing: 1px;
    font-size: 22px;
    font-weight: 500;
    color: $mediumDarkGray;
    text-transform: uppercase;
    p {
      margin-bottom: 8px;
    }
    .plan-subtitle {
      font-size: 12px;
      text-transform: none;
      color: $gray;
      margin-bottom: 0px;
    }
  }
  .billed {
    color: $mediumDarkGray;
    height: 24px;
  }
  .period-text {
    font-size: 10px;
    color: $mediumDarkGray;
    position: absolute;
    .discount {
      color: red;
      font-weight: 500;
      width: 26px;
      display: inline-block;
    }
    &.annual {
      width: 64px;
      left: 0;
    }
    &.monthly {
      right: 0;
    }
    &.active {
      color: #474747;
    }
  }
  .currency {
    font-size: 25px;
    color: $mediumDarkGray;
  }
  .price {
    font-size: 70px;
    color: $mediumDarkGray;
  }
  .price.small {
    font-size: 40px;
    align-self: baseline;
    margin-top: 15px;
  }
  .per-period {
    color: $mediumDarkGray;
  }
  .specs {
    color: gray;
    font-size: 12px;
    .btn-tooltip {
      background-color: #3cd699;
      border: none;
      height: 20px;
      width: 20px;
      border-radius: 200%;
      padding: 0;
      font-weight: 500;
      font-size: 13px;
    }
  }
  .details {
    color: $gray;
    font-weight: 400;
    font-size: 12px;
    border-bottom: 2px solid;
  }
  .no-switch {
    margin-top: 50px;
  }
  .subscribe-container {
    flex-grow: 1;
    .button-container {
      align-self: flex-end;
    }
  }
  .previous-cost {
    color: $mediumDarkGray;
    position: relative;
    height: 100%;
    padding: 0 5px;
    &:before {
      border-bottom: 2px solid var(--borderColor);
      position: absolute;
      left: 0;
      content: '';
      width: 100%;
      height: 50%;
    }
  }
}
</style>
